import {
    ClientSocketEvent,
    EnterRoomPayload,
    LeaveRoomPayload,
    Chips,
    RequestToSitOut,
    PlayerID,
    CheckPayload,
    CallPayload,
    CallAnyPayload,
    CheckOrFoldPayload,
    BetPayload,
    RaisePayload,
    FoldPayload,
    AllInPayload,
    PreCheckPayload,
    ResetPremovePayload,
    RequestToDontShowAlert,
    IGameState,
    AddTimePayload,
    RequestToForfeitPayload,
    DeductTimebankCountPayload,
    PreCallPayload,
    CreateServerConnectionPayload,
    IStrippedGameState,
    PokerRound
} from "@soposopo/sopo-models/dist";
import { distinctUntilChanged, map, mergeWith, shareReplay, switchMap, take, takeUntil } from "rxjs/operators";
import { NavController } from "@ionic/angular";
import { BehaviorSubject, firstValueFrom, interval, Observable, of, race, ReplaySubject, Subject, Subscription, timer } from "rxjs";
import { Socket } from "ngx-socket-io";
import { GameDialogService } from "src/app/game/page/game/gameDialog.service";
import { Injector } from "@angular/core";
import { SopoLoadingService } from "src/app/shared/services/sopo-loading/sopo-loading.service";
import { AuthStoreDispatcher } from "src/app/core/state/auth/auth.dispatcher";
import { GameStoreDispatcher } from "src/app/core/state/game/game.dispatcher";
import { GameSocketStoreDispatcher } from "src/app/core/state/gameSocket/gameSocket.dispatcher";
import { ProfileStateDispatcher } from "src/app/core/state/profile/profile.dispatcher";
import { GameStateId } from "src/app/core/state/game/game.state";
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { SopoAlertSettings } from "src/app/sopo-popup/interfaces/alert";
import { SopoConfirmSettings } from "src/app/sopo-popup/interfaces/confirm";
import { IMakeSidePredictionPayload } from "@soposopo/sopo-models/dist/socketPayload/TableHostPayload";
import { SopoSideBettingResponseData } from "src/app/sopo-popup/components/sopo-side-betting/sopo-side-betting.component";

export interface IDealerStatePayload {
    state: IGameState;
    tableID: string;
    deltaDealerSequenceNumber: number;
    dealerSequenceNumber?: number;
    handSequenceNumber?: number;
}

export interface IDeltaDealerPayload {
    diff: IStrippedGameState;
    tableID: string;
    deltaDealerSequenceNumber: number;
    dealerSequenceNumber?: number;
    handSequenceNumber?: number;
}


export abstract class GameServer {
    //SERVICES
    protected abstract injector: Injector;
    protected gameSocketsDispatcher: GameSocketStoreDispatcher;
    protected profileService: ProfileStateDispatcher;
    protected sopoPopupService: SopoPopupService;
    protected gameDispatcher: GameStoreDispatcher;
    protected auth: AuthStoreDispatcher;
    protected gameDialog: GameDialogService;
    protected loadingCtrl: SopoLoadingService;
    protected navCtrl: NavController;

    protected socket: Socket;
    protected abstract url: string;
    protected abstract gameStateId: GameStateId;

    private startTime: number;
    protected clientID: string;
    protected alertPresented: any;
    protected ping$: Subject<number> = new Subject();
    protected serverTime$: Subject<number> = new ReplaySubject(1);
    public gameState$: Subject<IDealerStatePayload> = new Subject<IDealerStatePayload>();
    public handState$: Subject<IGameState> = new Subject<IGameState>();
    public socketState$: Observable<{connected: boolean, socketId: string}>;

    private listening: boolean;
    private disconnectSubscription: Subscription;
    private dealerMessageSubscription: Subscription;
    private dealerResetSubscription: Subscription;
    private gameStateSubscription: Subscription;
    private handStateSubscription: Subscription;
    private connectionHandshakeDoneSubscription: Subscription;
    private gameSubscriptions: Subscription;
    private timebankSuccessMessageSubsription: Subscription;
    private timebankFailMessageSubsription: Subscription;
    private forcedDisconnectSubscription: Subscription;

    private disconnectTimeout: NodeJS.Timeout;
    private pingTimeoutTimer: NodeJS.Timeout;
    private nextPingTimeout: NodeJS.Timeout;
    private unansweredPingCount = 0;
    private deltaDealerSequenceNumber = 0;
    private processDealerEvents = false;
    private dealerSequenceNumber = 0;
    private handSequenceNumber = -1;

    private _retryToGetGameStateAfter = 250;
    private _gameStateRetryTimeout: NodeJS.Timeout;

    private _isRecoveringGameState = new BehaviorSubject(false);
    get isRecoveringGameState(): Observable<boolean> {
        return this._isRecoveringGameState.asObservable();
    }

    private _pingEventEmitted = new Subject<void>();
    private _realtimeLatencyObservable: Observable<number> = of(0).pipe(
        mergeWith(
            this._pingEventEmitted.asObservable().pipe(
                switchMap(() => {
                    const finalDelay = this.ping$.asObservable().pipe(
                        take(1),
                    );

                    const endTimer = timer(2500).pipe(
                        map(() => 2500),
                    );

                    const finalOrEnd = race([finalDelay, endTimer]);

                    const checkInterval = interval(100).pipe(
                        map(() => Date.now() - (this.startTime || Date.now())),
                        takeUntil(finalOrEnd),
                    );

                    return checkInterval.pipe(
                        mergeWith(finalOrEnd),
                    );
                })
            )
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    get realtimeLatencyObservable(): Observable<number> {
        return this._realtimeLatencyObservable;
    }

    constructor() {
    }

    protected injectServices(){
        this.gameSocketsDispatcher = this.injector.get(GameSocketStoreDispatcher);
        this.profileService = this.injector.get(ProfileStateDispatcher);
        this.sopoPopupService = this.injector.get(SopoPopupService);
        this.gameDispatcher = this.injector.get(GameStoreDispatcher);
        this.auth = this.injector.get(AuthStoreDispatcher);
        this.gameDialog = this.injector.get(GameDialogService);
        this.loadingCtrl = this.injector.get(SopoLoadingService);
        this.navCtrl = this.injector.get(NavController);
    }

    protected init() {
        this.alertPresented = false;
        const urlToHandle = new URL(this.url);
        const gameServerName = urlToHandle.pathname;
        const urlOriginPart = urlToHandle.origin;
        this.socket = new Socket({url: urlOriginPart, options:{path: gameServerName + "socket.io", reconnectionDelayMax: 1000}});
        this.injectServices();
        this.initializeSocketStateObservable();
        this.listenToCoreMessages();
        this.connect();
        this.disconnectTimeout = setTimeout(() => {
            this.gameSocketsDispatcher.disconnect(this.gameStateId);
        }, 10000);
        this.storeClientId();
        this.initPingListener();
    }

    async storeClientId() {
        this.clientID = await this.auth.getMyID();
    }

    private initPingListener() {
        this.socket.on('pongCheck', (serverTime) => {
            if (typeof serverTime === 'object') {
                const isServerAhead = serverTime.deltaDealerSequenceNumber !== undefined && (
                    this.deltaDealerSequenceNumber < serverTime.deltaDealerSequenceNumber ||
                    (this.deltaDealerSequenceNumber === Number.MAX_SAFE_INTEGER && serverTime.deltaDealerSequenceNumber !== this.deltaDealerSequenceNumber)
                );
                const isDealerServerAhead = serverTime.dealerSequenceNumber !== undefined && (
                    this.dealerSequenceNumber < serverTime.dealerSequenceNumber ||
                    (this.dealerSequenceNumber === Number.MAX_SAFE_INTEGER && serverTime.dealerSequenceNumber !== this.dealerSequenceNumber)
                );
                const isHandServerAhead = serverTime.handSequenceNumber !== undefined
                    && this.handSequenceNumber >= 0
                    && (
                        this.handSequenceNumber < serverTime.handSequenceNumber ||
                        (this.handSequenceNumber === Number.MAX_SAFE_INTEGER && serverTime.handSequenceNumber !== this.handSequenceNumber)
                    );
                if ((isServerAhead || isDealerServerAhead || isHandServerAhead) && this.processDealerEvents) {
                    console.debug('[GameServer] - Corrupt dealer state detected on pong response. Restoring state...');
                    this.restoreDealerState();
                }

                if (serverTime.pingId !== this.startTime) { // Ignoring the pongCheck response if the pingId does not match.
                    console.debug('Pong check ignored because of different pingId');
                    return;
                }
            }

            this.clearPingTimeout();
            this.unansweredPingCount = 0;

            const latency = Date.now() - this.startTime;
            this.ping$.next(latency);
            if (typeof serverTime === 'number') {
                this.serverTime$.next(serverTime);
            } else {
                this.serverTime$.next(serverTime.serverTime);
            }
            this.nextPingTimeout = setTimeout(() => {
                this.ping();
            }, latency > 1000 ? 0 : 1000 - latency);
        });
    }

    private handleConnected(): void {
        console.log('Game server -- connected - ', this.url);
        this.clearDisconnectTimeout();
        this.gameSocketsDispatcher.connect(this.gameStateId, this.socket.id);
    }

    private handleDisconnected(): void {
        console.log('Game server -- disconnected - ', this.url);
        this.clearPingTimeout();
        this.clearNextPingTimeout();
        this.unansweredPingCount = 0;
        this.ping$.next(0);
        this.processDealerEvents = false;
        this.handSequenceNumber = -1;
        this.clearGameStateRetryTimeout();
        this._isRecoveringGameState.next(false);
        this.gameSocketsDispatcher.disconnect(this.gameStateId);
        this.stopListeningToMessages();
    }

    private initializeSocketStateObservable(): void {
        this.socketState$ = this.gameSocketsDispatcher.getGameSocketStatusAndId(this.gameStateId).pipe(
           map(({connectionStatus, socketId}) => ({ connected: connectionStatus === 'connected', socketId })),
        );
    }

    public getSocketConnectionState(): Promise<{connected: boolean, socketId: string}> {
        return firstValueFrom(this.socketState$);
    }

    public connect() {
        this.getSocketConnectionState().then(({ connected, socketId }) => {
            if (!connected || (this.socket?.id && this.socket.id !== socketId)) {
                this.socket.connect();
            }
            if (!this.listening) {
                this.listenToMessages();
            }
        });
    }

    private ping() {
        const startTime = Date.now();
        if (Math.ceil(startTime / 1000) === Math.ceil(this.startTime / 1000)) return; //filter parallel pingchecks after restart
        this.startTime = startTime;
        this._pingEventEmitted.next();
        this.socket.emit('pingCheck', { pingId: this.startTime });
        this.startPingTimeout();
    }

    protected connectionHandshake() {
        firstValueFrom(this.profileService
            .getProfileState())
            .then(async (profile) => {
                if (profile.profileData && profile.profileData.uid != null) {
                    const idToken = await this.auth.getIdToken();
                    this.socket.emit('connectionHandshake', {
                        userID: profile.profileData.uid,
                        gameID: this.gameStateId,
                        displayName: profile.profileData.displayName,
                        idToken
                    } as CreateServerConnectionPayload);
                } else {
                    const options: SopoAlertSettings = {
                        header: `Failed to connect to server.`,
                        message: `Connection Handshake failed.`,
                        buttonText: `Try Again`,
                        buttonHandler: () => {
                            this.getSocketAndConnect();
                        },
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME
                    };
                    this.sopoPopupService.showAlert('game', options);
                }
            });
    }

    private unsubscribeFromGameSubscriptions() {
        if (this.gameSubscriptions === undefined || this.gameSubscriptions.closed) return;
        this.gameSubscriptions.unsubscribe();
    }

    setGameState(routeID: string, gameStateId: GameStateId, clientId: string) {
        if (this.gameSubscriptions && !this.gameStateSubscription.closed) {
            return;
        }

        this.gameSubscriptions = new Subscription();

         // UPDATE GAME STATE BASED ON DEALER RESPONSE FROM SOCKET
        const gameStateSubscription = this.getGameSate().subscribe((dealer) => {
            if (dealer && dealer.tableID === routeID) {
                const gameStateDeltaDealerSequenceNumber = dealer.deltaDealerSequenceNumber;
                const gameStateDealerSequenceNumber = dealer?.dealerSequenceNumber;
                const gameStateHandSequenceNumber = dealer?.handSequenceNumber;
                if (
                    !this.processDealerEvents ||
                    this.checkSequenceNumbers({
                        deltaDealerSequenceNumber: gameStateDeltaDealerSequenceNumber,
                        dealerSequenceNumber: gameStateDealerSequenceNumber,
                        handSequenceNumber: gameStateHandSequenceNumber,
                    })
                ) {
                    this.clearGameStateRetryTimeout();
                    this._isRecoveringGameState.next(false);
                    this.gameDispatcher.loadGameState(dealer.state, clientId, gameStateId);
                    if (!this.processDealerEvents) {
                        this.setDealerSequenceNumber(gameStateDealerSequenceNumber);
                        this.setHandSequenceNumber(gameStateHandSequenceNumber);
                        this.setDeltaDealerSequenceNumber(gameStateDeltaDealerSequenceNumber);
                        this.processDealerEvents = true;
                    }
                }
            }
        });

        const gameDeltaStateSubscription = this.getDeltaGameState().subscribe(async (gameState) => {
            if (gameState && gameState.tableID === routeID) {
                const gameStateDeltaDealerSequenceNumber = gameState?.deltaDealerSequenceNumber;
                const gameStateDealerSequenceNumber = gameState?.dealerSequenceNumber;
                const gameStateHandSequenceNumber = gameState?.handSequenceNumber;
                if (this.checkSequenceNumbers({
                    dealerSequenceNumber: gameStateDealerSequenceNumber,
                    deltaDealerSequenceNumber: gameStateDeltaDealerSequenceNumber,
                    handSequenceNumber: gameStateHandSequenceNumber,
                })) {
                    this.gameDispatcher.updateDeltaGameState(gameState.diff, clientId, gameStateId);
                }
            }
        });

        // UPDATE Hand STATE BASED ON DEALER RESPONSE FROM SOCKET
        const handStateSubscription = this.getHandSate().subscribe((hand) => {
            if (hand && hand['tableId'] === routeID && hand['playerID'] === clientId) {
                const handDealerSequenceNumber = hand?.['dealerSequenceNumber'];
                const handHandSequenceNumber = hand?.['handSequenceNumber'];
                if (this.checkSequenceNumbers({
                    dealerSequenceNumber: handDealerSequenceNumber,
                    handSequenceNumber: handHandSequenceNumber,
                })) {
                    this.gameDispatcher.loadHandState(hand['hand'], clientId, gameStateId);
                }
            }
        });
        this.gameSubscriptions.add(gameStateSubscription);
        this.gameSubscriptions.add(gameDeltaStateSubscription);
        this.gameSubscriptions.add(handStateSubscription);
    }

    private checkSequenceNumbers(sequenceNumbers: { dealerSequenceNumber?: number, deltaDealerSequenceNumber?: number, handSequenceNumber?: number }): boolean {
        if (!this.processDealerEvents) {
            return false;
        }
        if (sequenceNumbers.dealerSequenceNumber !== undefined) {
            const dealerValid = this.checkAndSetDealerSequenceNumber(sequenceNumbers.dealerSequenceNumber);
            if (!dealerValid) {
                return false;
            }
        }
        if (sequenceNumbers.handSequenceNumber !== undefined) {
            const handValid = this.checkAndSetHandSequenceNumber(sequenceNumbers.handSequenceNumber);
            if (!handValid) {
                return false;
            }
        }
        if (sequenceNumbers.deltaDealerSequenceNumber !== undefined) {
            const deltaDealerValid = this.checkAndSetDeltaDealerSequenceNumber(sequenceNumbers.deltaDealerSequenceNumber);
            if (!deltaDealerValid) {
                return false;
            }
        }
        return true;
    }

    private checkAndSetDeltaDealerSequenceNumber(deltaDealerSequenceNumber: number): boolean {
        if (
            deltaDealerSequenceNumber === this.deltaDealerSequenceNumber ||
            deltaDealerSequenceNumber - this.deltaDealerSequenceNumber === 1  ||
            (this.deltaDealerSequenceNumber === Number.MAX_SAFE_INTEGER && deltaDealerSequenceNumber === 0)
        ) {
            this.setDeltaDealerSequenceNumber(deltaDealerSequenceNumber);
            return true;
        }
        this.handleCorruptSequenceNumber('delta dealer');
        return false;
    }

    private checkAndSetDealerSequenceNumber(dealerSequenceNumber: number): boolean {
        if (
            dealerSequenceNumber === this.dealerSequenceNumber ||
            dealerSequenceNumber - this.dealerSequenceNumber === 1  ||
            (this.dealerSequenceNumber === Number.MAX_SAFE_INTEGER && dealerSequenceNumber === 0)
        ) {
            this.setDealerSequenceNumber(dealerSequenceNumber);
            return true;
        }
        this.handleCorruptSequenceNumber('dealer');
        return false;
    }

    private checkAndSetHandSequenceNumber(handSequenceNumber: number): boolean {
        if (
            this.handSequenceNumber === -1 ||
            handSequenceNumber === this.handSequenceNumber ||
            handSequenceNumber - this.handSequenceNumber === 1 ||
            (this.handSequenceNumber === Number.MAX_SAFE_INTEGER && handSequenceNumber === 0)
        ) {
            this.setHandSequenceNumber(handSequenceNumber);
            return true;
        }
        this.handleCorruptSequenceNumber('hand');
        return false;
    }

    private handleCorruptSequenceNumber(label: string): void {
        console.debug(`[GameServer] - Corrupt ${label} state detected. Restoring state...`);
        this.restoreDealerState();
    }

    private listenToCoreMessages() {
        this.socket.on('connect', () => {
            this.getSocketAndConnect();
        })
        this.socket.on('reconnect', () => {
            this.getSocketAndConnect();
        })
        this.socket.on("connect_error", (err) => {
            console.debug(`connect_error due to ${err.message}`);
        });
    }

    protected listenToMessages() {
        this.listening = true;
        this.connectionHandshakeDoneSubscription = this.getConnectionHandshakeDone().subscribe(() => {
            this.handleConnected();
        })
        this.disconnectSubscription = this.getOnDisonnect().subscribe(() => {
            this.handleDisconnected();
        });
        this.dealerMessageSubscription = this.getDealerMessage().subscribe((dealer) => {
            this.gameSocketsDispatcher.acceptDealerSignal(dealer.message);
        });
        this.gameStateSubscription = this.getGameState().subscribe((gameState) => {
            this.gameState$.next(gameState);
        });
        this.handStateSubscription = this.getHandState().subscribe((handState) => {
            this.handState$.next(handState);
        });
        this.dealerResetSubscription = this.getDealerReset().subscribe((reset) => {
            if (this.checkSequenceNumbers({ dealerSequenceNumber: reset?.dealerSequenceNumber, handSequenceNumber: reset?.handSequenceNumber })) {
                this.gameDispatcher.resetDealer(reset.tableID);
            }
        });
        this.timebankSuccessMessageSubsription = this.getTimebankSuccessMessage().subscribe(data => {
            this.showPopupAcccordingToView(data);
        });
        this.timebankFailMessageSubsription = this.getTimebankFailMessage().subscribe(data => {
            this.showPopupAcccordingToView(data);
        });
        this.forcedDisconnectSubscription = this.socket.fromEvent('forcedDisconnect')
            .subscribe((data: { message?: string }) => this.handleForcedDisconnect(data));
    }

    private async handleForcedDisconnect(data: { message?: string }) {
        const options: SopoConfirmSettings = {
            data: {
                header: 'Disconnected',
                body: data?.message || 'You have been disconnected because you connected from another device.',
                okText: 'Reconnect',
                cancelText: 'Close',
            },
        };
        const reconnect = await this.sopoPopupService.promptForConfirmation('game', options, this.gameStateId);
        if (reconnect) {
            this.connect();
        } else {
            this.gameDispatcher.removeGame(this.gameStateId);
        }
    }

    protected getSocketAndConnect() {
        this.getSocketConnectionState().then(({connected, socketId})=> {
            if (!connected || (this.socket?.id && this.socket.id !== socketId)) {
                this.ping();
                this.connectionHandshake();
            }
            if (!this.listening) {
                this.listenToMessages();
            }
        });
    }

    protected async getAlertMessage(data){
        if (data.header && data.message) {
            await this.showAlert(data.header, data.message);
        } else if (data.header && data.reason) {
            await this.showAlert(data.header, data.reason);
        } else {
            await this.showAlert('Error', 'Failed to execute the command');
        }
    }

    private async showAlert(header: string, message: string) {
        this.sopoPopupService
            .showAlert('game', {
                header: header,
                message: message,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            });
    }

    //EMITTERS

    async triggerAnimation(sourceID: string, targetID: string, emotoes: any, tableID: string) {
        const isConnected = (await this.getSocketConnectionState()).connected;
        if (!isConnected) return Promise.reject();
        const payload = { sourceID, targetID, emotoes, tableID };
        this.socket.emit(ClientSocketEvent.EMOTOES_ANIMATION, payload);
        return Promise.resolve();
    }

    async enterRoom(tableID: string, clubID: string): Promise<void> {
        const profileState = await firstValueFrom(this.profileService.getProfileState());
        const payload: EnterRoomPayload = { userID: profileState.profileData.uid, tableID, clubID, displayName: profileState.profileData.displayName };
        this.socket.emit(ClientSocketEvent.ENTER_ROOM, payload);
        return Promise.resolve();
    }

    async leaveRoom(tableID: string): Promise<void> {
        const userID = await this.profileService.getMyID();
        const payload: LeaveRoomPayload = { userID, tableID };
        this.socket.emit(ClientSocketEvent.LEAVE_ROOM, payload);
        return Promise.resolve();
    }

    async enterUserRoom(): Promise<void> {
        const userID = await this.profileService.getMyID();
        const payload = { userID };
        this.socket.emit(ClientSocketEvent.ENTER_USER_ROOM, payload);
        return Promise.resolve();
    }

    async leaveUserRoom(): Promise<void> {
        const userID = await this.profileService.getMyID();
        const payload = { userID };
        this.socket.emit(ClientSocketEvent.LEAVE_USER_ROOM, payload);
        return Promise.resolve();
    }

    async initiateGameState(tableID: string) {
        this.clearGameStateRetryTimeout();
        this.initiateGameStateWithRetry(tableID);
    }

    async check(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: CheckPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.CHECK, payload);
    }

    async call(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: CallPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.CALL, payload);
    }

    async callAny(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: CallAnyPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.CALL_ANY, payload);
    }

    async preCall(tableID: string, playerID: PlayerID, wagerToCall: Chips, handId?: string, pokerRound?: PokerRound) {
        const payload: PreCallPayload = { tableID, playerID, wagerToCall, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.PRE_CALL, payload);
    }

    async checkOrFold(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: CheckOrFoldPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.CHECK_OR_FOLD, payload);
    }

    async bet(tableID: string, playerID: PlayerID, bet: Chips, handId?: string, pokerRound?: PokerRound) {
        const payload: BetPayload = { tableID, playerID, bet, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.BET, payload);
    }

    async raise(tableID: string, playerID: PlayerID, raise: Chips, handId?: string, pokerRound?: PokerRound) {
        const payload: RaisePayload = { tableID, playerID, raise, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.RAISE, payload);
    }

    async fold(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: FoldPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.FOLD, payload);
    }

    async allIn(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: AllInPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.ALL_IN, payload);
    }

    async preCheck(tableID: string, playerID: PlayerID, handId?: string, pokerRound?: PokerRound) {
        const payload: PreCheckPayload = { tableID, playerID, handId, pokerRound };
        this.socket.emit(ClientSocketEvent.PRE_CHECK, payload);
    }

    async resetPremove(tableID: string, playerID: string) {
        const payload: ResetPremovePayload = { tableID, playerID };
        this.socket.emit(ClientSocketEvent.RESET_PREMOVE, payload);
    }

    async revealCards(tableID: string, playerID: string, playerCards) {
        const payload: any = { tableID, playerID, playerCards };
        this.socket.emit(ClientSocketEvent.REVEAL_CARDS, payload);
    }

    async doNotShowAlert(tableID: string, userID: string, isRequested: boolean) {
        const payload: RequestToDontShowAlert = { tableID, userID, isRequested };
        this.socket.emit(ClientSocketEvent.DO_NOT_SHOW_ALERT, payload);
    }

    async requestComeBack(tableID: any, userID: any) {
        const payload: RequestToSitOut = { tableID, userID };
        this.socket.emit(ClientSocketEvent.COME_BACK, payload, () => Promise.resolve());
    }

    async requestSitOut(tableID: string, userID: string) {
        const payload: RequestToSitOut = { tableID, userID };
        this.socket.emit(ClientSocketEvent.SIT_OUT, payload, () => Promise.resolve());
    }

    async setGPS(latitude: number, longitude: number, tableID: string, playerIDTemp: string) {
        const playerID = playerIDTemp === undefined ? this.clientID : playerIDTemp;
        const payload = { latitude, longitude, tableID, playerID };
        this.socket.emit(ClientSocketEvent.SET_GPS_COORDINATES, payload);
    }

    async deductUserEmotesCount(userID: string) {
        const payload = { userID };
        this.socket.emit(ClientSocketEvent.DEDUCT_EMOTES_COUNT, payload);
        return Promise.resolve();
    }

    async deductTimebankCount(tableId:string, userId:string) {
        const payload: DeductTimebankCountPayload = {tableId, userId};
        this.socket.emit(ClientSocketEvent.DEDUCT_TIMEBANK_COUNT, payload);
    }

    async makeSidePrediction(sidePredictionPayload: SopoSideBettingResponseData, handId: string, userId: string) {
        const payload: IMakeSidePredictionPayload = {
            userId,
            amountBet: sidePredictionPayload.amount,
            predictedColor: sidePredictionPayload.color,
            handId,
        };
        this.socket.emit(ClientSocketEvent.MAKE_SIDE_PREDICTION, payload);
    }

    //NOT IN USE
    async forceFold(tableID: string, playerID: string) {
        const payload: any = { tableID, playerID };
        this.socket.emit(ClientSocketEvent.FORCE_FOLD_BY_TIME_OUT, payload);
    }
    //NOT IN USE
    requestToForfeit(tableID: string, userID: string) {
        const payload: RequestToForfeitPayload = { tableID, userID, isTableDeleted: false };
        this.socket.emit(ClientSocketEvent.FORFEIT, payload);
    }

    //NOT IN USE
    addTime(tableID: string, playerID: PlayerID) {
        const payload: AddTimePayload = { tableID, playerID };
        this.socket.emit(ClientSocketEvent.ADD_TIME, payload);
    }

    //NOT IN USE
    getMessage(): Observable<any> {
        return this.socket.fromEvent('message');
    }

    private getConnectionHandshakeDone(): Observable<any> {
        return this.socket.fromEvent('connectionHandshakeDone');
    }

    private getOnDisonnect(): Observable<any> {
        return this.socket.fromEvent('disconnect');
    }

    private getDealerMessage(): Observable<any> {
        return this.socket.fromEvent('dealerMessage');
    }

    private getGameState(): Observable<any> {
        return this.socket.fromEvent('gameState');
    }

    private getHandState(): Observable<any> {
        return this.socket.fromEvent('handState');
    }

    private getDealerReset(): Observable<any> {
        return this.socket.fromEvent('dealerReset');
    }

    getPing(): Observable<number> {
        return this.ping$;
    }

    getTableMessage(): Observable<any> {
        return this.socket.fromEvent('tableMessage');
    }

    getServerTimeObservable(): Observable<number> {
        return this.serverTime$.asObservable().pipe(distinctUntilChanged());
    }

    getGameSate(): Observable<IDealerStatePayload> {
        return this.gameState$;
    }

    getDeltaGameState(): Observable<IDeltaDealerPayload> {
        return this.socket.fromEvent('gameDeltaState');
    }
    getHandSate(): Observable<IGameState> {
        return this.handState$;
    }

    getDealerError(): Observable<any> {
        return this.socket.fromEvent('dealerError');
    }

    private getTimebankSuccessMessage(): Observable<any> {
        return this.socket.fromEvent('timebankSuccessMessage');
    }
    private getTimebankFailMessage(): Observable<any> {
        return this.socket.fromEvent('timebankFailMessage');
    }

    /**
     * Disconnect to the websocket and unsubscribe from socket events
     */
    public disconnect() {
        this.clearDisconnectTimeout();
        this.leaveUserRoom();
        this.unsubscribeFromGameSubscriptions();
        this.handleDisconnected();
        this.socket.removeAllListeners('connect');
        this.socket.removeAllListeners('reconnect');
        this.socket.removeAllListeners('connect_error');
        this.socket.removeAllListeners('pongCheck');
        this.socket.disconnect();
    }

    private async showPopupAcccordingToView(data) {
        const gameStateId = await this.gameDispatcher.getGameStateId(data.tableID);
        if (gameStateId != null) {
            this.sopoPopupService.showAlert(
                "game-container",
                {
                    header: data.header,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
                gameStateId,
            );
        } else {
            this.sopoPopupService.showAlert(
                "game",
                {
                    header: data.header,
                    message: data.message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                },
            );
        }
    }



    /**
     * Unsubscribe from various socket events [onConnect, onDisconnect, tableMessage, dealerMessage]
     */

    protected stopListeningToMessages() {
        if (this.connectionHandshakeDoneSubscription) {
            this.connectionHandshakeDoneSubscription.unsubscribe();
        }
        if (this.disconnectSubscription) {
            this.disconnectSubscription.unsubscribe();
        }
        if (this.dealerMessageSubscription) {
            this.dealerMessageSubscription.unsubscribe();
        }
        if (this.gameStateSubscription) {
            this.gameStateSubscription.unsubscribe();
        }
        if (this.handStateSubscription) {
            this.handStateSubscription.unsubscribe();
        }
        if (this.dealerResetSubscription) {
            this.dealerResetSubscription.unsubscribe();
        }
        if (this.timebankSuccessMessageSubsription) {
            this.timebankSuccessMessageSubsription.unsubscribe();
        }
        if (this.timebankFailMessageSubsription) {
            this.timebankFailMessageSubsription.unsubscribe();
        }
        if (this.forcedDisconnectSubscription) {
            this.forcedDisconnectSubscription.unsubscribe();
        }
        this.listening = false;
    }

    private clearDisconnectTimeout() {
        clearTimeout(this.disconnectTimeout);
    }

    private initiateGameStateWithRetry(tableID: string) {
        this.loadGameState(tableID);

        this._gameStateRetryTimeout = setTimeout(() => {
            this.initiateGameStateWithRetry(tableID);
        }, this._retryToGetGameStateAfter);

        if (this._retryToGetGameStateAfter < 1000) { // Increment the retryToGetGameStateAfter time by 250ms for each retry, up to a maximum of 1000ms.
            this._retryToGetGameStateAfter += 250;
        }
    }

    private loadGameState(tableID: string) {
        const payload = { tableID };
        this.socket.emit(ClientSocketEvent.LOAD_GAME_STATE, payload, () => Promise.resolve());
    }

    private clearGameStateRetryTimeout() {
        clearTimeout(this._gameStateRetryTimeout);
        if (this._retryToGetGameStateAfter !== 250) {
            this._retryToGetGameStateAfter = 250; // Setting the _retryToGetGameStateAfter to 250ms for the next retry cycle.
        }
    }

    private setDeltaDealerSequenceNumber(sequenceNumber?: number) {
        if (sequenceNumber !== undefined) {
            this.deltaDealerSequenceNumber = sequenceNumber;
        }
    }

    private setDealerSequenceNumber(sequenceNumber?: number): void {
        if (sequenceNumber !== undefined) {
            this.dealerSequenceNumber = sequenceNumber;
        }
    }

    private setHandSequenceNumber(sequenceNumber?: number): void {
        if (sequenceNumber !== undefined) {
            this.handSequenceNumber = sequenceNumber;
        }
    }

    private restoreDealerState() {
        this._isRecoveringGameState.next(true);
        this.processDealerEvents = false;
        this.gameDispatcher.resetDealerByGameStateId(this.gameStateId);
        this.initiateGameState(this.gameStateId);
    }

    private startPingTimeout() {
        this.clearPingTimeout();
        this.pingTimeoutTimer = setTimeout(() => {
            this.unansweredPingCount++;
            if (this.unansweredPingCount >= 2) {
                console.debug('emiting 3000 ping value');
                this.ping$.next(3000);
            }
            this.ping();
        }, 3000);
    }

    private clearPingTimeout() {
        clearTimeout(this.pingTimeoutTimer);
    }

    private clearNextPingTimeout() {
        clearTimeout(this.nextPingTimeout);
    }
}
